import { useCallback, useRef, useState } from 'react';
import { IconCheck, IconCopy, IconPhotoUp } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import Hint from '../../../../../components/common/Hint';
import ColorPickerCanvas from './ColorPickerCanvas';
import {
    ACCEPT,
    MAX_FILE_BYTES,
    isSupportedColorPickerFile,
} from './colorPickerImageEngine';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';

function CopyValueRow({ label, value, onCopy, copied, copyLabel }) {
    return (
        <div className="flex items-center gap-2 rounded-xl border border-[#ECE8E4] bg-white px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900">
            <div className="min-w-0 flex-1 leading-snug">
                <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[#A8A099] dark:text-zinc-500">
                    {label}
                </div>
                <div className="truncate font-mono text-[13px] font-semibold text-[#1C1815] dark:text-zinc-100">
                    {value}
                </div>
            </div>
            <button
                type="button"
                onClick={onCopy}
                aria-label={copyLabel}
                className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg transition-colors ${
                    copied
                        ? 'bg-[#E3F6EC] text-[#1E9E5E] dark:bg-emerald-950/30 dark:text-emerald-400'
                        : 'bg-[#F7F5F3] text-[#6B635C] hover:bg-amber-50 hover:text-amber-700 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:text-amber-400'
                }`}
            >
                {copied ? <IconCheck size={15} stroke={2.2} /> : <IconCopy size={15} stroke={1.9} />}
            </button>
        </div>
    );
}

const MAX_RECENT = 12;

function luminance(r, g, b) {
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function PaletteSwatch({ color, active, onClick }) {
    const isLight = luminance(color.r, color.g, color.b) > 0.5;

    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-xl border p-1.5 text-left transition-colors ${
                active
                    ? 'border-amber-500 ring-2 ring-amber-400/40'
                    : 'border-[#ECE8E4] hover:border-amber-400/40 dark:border-zinc-700'
            }`}
        >
            <div
                className="flex h-6 items-center justify-center rounded-md"
                style={{ backgroundColor: color.hex }}
            >
                <span className={`text-[11px] font-bold ${isLight ? 'text-black' : 'text-white'}`}>
                    {color.hex}
                </span>
            </div>
            <p className="mt-1 truncate text-center font-mono text-[10.5px] leading-tight text-[#A8A099] dark:text-zinc-500">
                {color.r}, {color.g}, {color.b}
            </p>
            <p className="truncate text-center font-mono text-[10.5px] leading-tight text-[#A8A099] dark:text-zinc-500">
                {color.h}, {color.s}%, {color.l}%
            </p>
        </button>
    );
}

export default function ColorPickerImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const usageRecordedRef = useRef(false);
    const copyTimerRef = useRef(null);

    const [file, setFile] = useState(null);
    const [picked, setPicked] = useState(null);
    const [palette, setPalette] = useState([]);
    const [recent, setRecent] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState(null);
    const [copiedKey, setCopiedKey] = useState(null);

    const addFile = useCallback((incoming) => {
        const next = Array.from(incoming || [])[0];
        if (!next) return;

        if (!isSupportedColorPickerFile(next)) {
            const msg = t('colorPickerImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        if (next.size > MAX_FILE_BYTES) {
            const msg = t('colorPickerImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        usageRecordedRef.current = false;
        setFile(next);
        setPicked(null);
        setPalette([]);
        setError(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied picture straight in.
    useImagePaste(addFile);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        usageRecordedRef.current = false;
        setFile(null);
        setPicked(null);
        setPalette([]);
        setRecent([]);
        setError(null);
        setCopiedKey(null);
    };

    const handleLoadError = useCallback(() => {
        const msg = t('colorPickerImage.errors.loadFailed');
        setFile(null);
        setError(msg);
        notifications.show({ title: t('common.error'), message: msg, color: 'red' });
    }, [t]);

    const handlePaletteReady = useCallback((colors) => {
        setPalette(colors);
        if (colors.length > 0) {
            setPicked(colors[0]);
        }
    }, []);

    const handlePick = useCallback((color) => {
        if (!usageRecordedRef.current) {
            usageRecordedRef.current = true;
            recordToolUsage('image/color-picker', { category: 'image' });
        }

        setPicked(color);
        setRecent((prev) => {
            const next = [color, ...prev.filter((c) => c.hex !== color.hex)];
            return next.slice(0, MAX_RECENT);
        });
    }, []);

    const handleCopy = (key, value) => {
        const ok = copyTextToClipboard(value);
        if (!ok) {
            void navigator.clipboard?.writeText(value);
        }

        setCopiedKey(key);
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopiedKey(null), 1600);
        notifications.show({
            title: t('common.success'),
            message: t('colorPickerImage.copied', { value }),
            color: 'green',
        });
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file,
        processing: false,
        hasResult: !!picked,
        hasError: !!error,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('colorPickerImage.statusPick'),
        success: t('colorPickerImage.statusPicked'),
    });

    return (
        <ToolPageLayout
            slug="image/color-picker"
            subtitleKey="colorPickerImage.subtitle"
            status={status}
            statusText={statusText}
            onReset={file || error ? handleReset : undefined}
            // Nothing to run — the picture is the input and the codes are the output — so
            // the bar's main button is the one thing left to do: pick (or swap) the picture.
            primaryAction={{
                label: file ? t('colorPickerImage.changeImage') : t('colorPickerImage.uploadTitle'),
                onClick: () => fileInputRef.current?.click(),
                icon: IconPhotoUp,
            }}
            showTrust={false}
            settings={(
                <div className="flex flex-col gap-3">
                    {/* The picture is chosen in the preview panel, so the left
                        column carries the colours only — this input is what both
                        the empty state and the button below open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    <ToolCard title={t('colorPickerImage.pickedTitle')}>
                        {picked ? (
                            <div className="space-y-2.5">
                                <div
                                    className="h-16 w-full rounded-xl border border-[#ECE8E4] dark:border-zinc-700"
                                    style={{ backgroundColor: picked.hex }}
                                />
                                <CopyValueRow
                                    label="HEX"
                                    value={picked.hex}
                                    copied={copiedKey === 'hex'}
                                    onCopy={() => handleCopy('hex', picked.hex)}
                                    copyLabel={t('colorPickerImage.copy')}
                                />
                                <CopyValueRow
                                    label="RGB"
                                    value={picked.rgbText}
                                    copied={copiedKey === 'rgb'}
                                    onCopy={() => handleCopy('rgb', picked.rgbText)}
                                    copyLabel={t('colorPickerImage.copy')}
                                />
                                <CopyValueRow
                                    label="HSL"
                                    value={picked.hslText}
                                    copied={copiedKey === 'hsl'}
                                    onCopy={() => handleCopy('hsl', picked.hslText)}
                                    copyLabel={t('colorPickerImage.copy')}
                                />
                            </div>
                        ) : (
                            <p className="text-[12.5px] text-[#A8A099] dark:text-zinc-500">
                                {file ? t('colorPickerImage.pickedEmptyReady') : t('colorPickerImage.pickedEmpty')}
                            </p>
                        )}
                    </ToolCard>

                    {palette.length > 0 ? (
                        <ToolCard title={t('colorPickerImage.paletteTitle', { count: toBnDigits(palette.length) })}>
                            <div className="grid grid-cols-3 gap-2">
                                {palette.map((color, index) => (
                                    <PaletteSwatch
                                        key={`${color.hex}-${index}`}
                                        color={color}
                                        active={picked?.hex === color.hex}
                                        onClick={() => setPicked(color)}
                                    />
                                ))}
                            </div>
                        </ToolCard>
                    ) : null}

                    {recent.length > 0 ? (
                        <ToolCard title={t('colorPickerImage.recentTitle')}>
                            <div className="grid grid-cols-6 gap-2">
                                {recent.map((color) => (
                                    <Hint label={color.hex} key={color.hex}>
                                    <button
                                        type="button"
                                        onClick={() => setPicked(color)}
                                        className={`aspect-square w-full rounded-lg border transition-transform hover:scale-110 ${
                                            picked?.hex === color.hex
                                                ? 'border-amber-500 ring-2 ring-amber-400/40'
                                                : 'border-[#ECE8E4] dark:border-zinc-700'
                                        }`}
                                        style={{ backgroundColor: color.hex }}
                                        aria-label={color.hex}
                                    />
                                    </Hint>
                                ))}
                            </div>
                        </ToolCard>
                    ) : null}
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('colorPickerImage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('colorPickerImage.previewEmpty')}
                    idleHint={t('colorPickerImage.previewEmptyHint')}
                    error={error}
                    canvasTone="plain"
                    showPasteHint
                    showIdle={!file && !error}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                >
                    {file ? (
                        <ColorPickerCanvas
                            file={file}
                            alt={t('colorPickerImage.previewAlt')}
                            onPick={handlePick}
                            onPaletteReady={handlePaletteReady}
                            onLoadError={handleLoadError}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
